import PropTypes from 'prop-types';
import * as R from 'ramda';
import React, {Component} from 'react';
import classNames from 'classnames';

import {exists} from 'js/common/object_helper';
import * as PostProps from 'js/common/proptypes/post_props';
import {ENUMS} from 'js/common';
import StoryGridItem from './story_grid_item';

// Components
import RatingSection from './rating_section';
import ThumbnailCenter from './thumbnail_center';
import LikesCommentsSection from './likes_comments_section';
import UsernameSection from './username_section';
import {formatInsightNumber, getMetricStringValue, insightNumberToPercent} from "../../../../../common/number_helper";

function formatDate(date, options) {
    return date.toLocaleDateString('en-US', options);
}

export default class PostGridItem extends Component {
    static propTypes = {
        currentCardSize: PropTypes.string.isRequired,
        post: PropTypes.shape(PostProps.POST),
        renderForPdf: PropTypes.bool,
        setPostScore: PropTypes.func.isRequired,
        showPostScore: PropTypes.bool.isRequired,
        setSelectedPost: PropTypes.func.isRequired,
        setSelectedPostAndComment: PropTypes.func.isRequired,
        showKoer: PropTypes.number.isRequired,
        showViews: PropTypes.number.isRequired,
        showAvgScore: PropTypes.bool.isRequired,
        getCompositionMetricsByCompositionId: PropTypes.func
    };

    static defaultProps = {
        showAvgScore: false,
        getCompositionMetricsByCompositionId: () => null
    };

    handleClickScore = (e, score) => {
        e.preventDefault();
        e.stopPropagation();

        const {setPostScore, setSelectedPostAndComment, post} = this.props;
        setPostScore(score, post.post_id);
        setSelectedPostAndComment(post.content_metadata_id);
    };

    displayPlayButtonOverlay = () => {
        const {content_type} = this.props.post;

        if (content_type === ENUMS.ContentType.VIDEO) {
            return <div className="play-button-overlay" />;
        }

        return null;
    };

    displayThumbnailCenter = () => {
        const {showKoer, post} = this.props;
        const {content_distribution_metadata, koer, engagement_rate} = post;

        if (exists(content_distribution_metadata)) {
            return (
                <ThumbnailCenter
                    koer={koer}
                    showKoer={showKoer}
                    engagementRate={engagement_rate}
                />
            );
        }

        return null;
    };

    displayLikesCommentSection = () => {
        const {content_distribution_metadata} = this.props.post;

        if (exists(content_distribution_metadata)) {
            const shouldFavoritesBeLikes =
                content_distribution_metadata.distribution_platform === ENUMS.CampaignType.TWITTER;

            const likeLeaf = shouldFavoritesBeLikes ? 'favorites' : 'likes';
            const likesPath = ['snapshots', 'social_engagements', likeLeaf];
            const likes = R.path(likesPath, content_distribution_metadata);

            const replies = R.path(['snapshots', 'social_engagements', 'replies'])(
                content_distribution_metadata
            );

            return (
                <LikesCommentsSection
                    likes={likes}
                    replies={replies}
                    renderForPdf={this.props.renderForPdf}
                />
            );
        }

        return null;
    };

    displayUsernameSection = () => {
        const {profile_image_url, content_distribution_metadata} = this.props.post;

        if (exists(content_distribution_metadata)) {
            return (
                <UsernameSection
                    avatarUrl={profile_image_url}
                    createdAt={content_distribution_metadata.created_at}
                    platformHandle={
                        content_distribution_metadata.snapshots.platform_account.platform_handle
                    }
                />
            );
        }
    };

    displayRatingsSection = () => {
        const {showPostScore, showAvgScore} = this.props;
        const {post_user_score, avg_post_user_score} = this.props.post;
        const avgScoreShown = showAvgScore ? avg_post_user_score : null;

        if (showPostScore) {
            return (
                <RatingSection
                    handleClickScore={this.handleClickScore}
                    postUserScore={post_user_score}
                    avgUserScore={avgScoreShown}
                />
            );
        }
    };

    getCompositionMetrics = () => {
        const {getCompositionMetricsByCompositionId} = this.props;
        const compositionId = R.path(
            [
                'post',
                'content_distribution_metadata',
                'extra',
                'snapi_etl_source',
                'composition_id'
            ],
            this.props
        );

        if (!compositionId) {
            return null;
        }

        const compositionMetrics = getCompositionMetricsByCompositionId(compositionId);

        return compositionMetrics;
    };

    // if supporting other types of cards we may want to make this generic
    shouldShowStoryCard = () => {
        return !!this.getCompositionMetrics();
    };

    render() {
        const {currentCardSize, setSelectedPost} = this.props;

        const {content_metadata_id, content_type, thumbnail_url} = this.props.post;
        const isDistributedContent = !!this.props.post?.content_distribution_metadata;
        const postData = this.props.post;
        const socialEngagements = postData?.content_distribution_metadata?.snapshots?.social_engagements ?? {};
        const postedAt = postData?.content_distribution_metadata?.created_at;
        const rating = postData.avg_creator_score || 5;

        const playButtonOverlay = this.displayPlayButtonOverlay();
        const likesCommentsSection = this.displayLikesCommentSection();
        const thumbnailCenter = this.displayThumbnailCenter();
        const ratingSection = this.displayRatingsSection();

        const postGridItemClasses = classNames('post', currentCardSize, content_type);
        const postStyle = {
            backgroundImage: `url(${thumbnail_url})`
        };

        if (this.shouldShowStoryCard()) {
            return (
                <StoryGridItem {...this.props} compositionMetrics={this.getCompositionMetrics()} />
            );
        }

        if (isDistributedContent) {
            return (
                <div className={postGridItemClasses} style={postStyle}>
                    {playButtonOverlay}
                    <div className="post-overlay">
                        <div className="ContentItemHoverWrapper">
                            <div className="ContentItemEngagementWrapper">
                                <div className="contentItemInterationItem">
                                    <div>{insightNumberToPercent(postData.engagement_rate)}</div>
                                </div>
                                <div>Engagement Rate</div>
                            </div>
                            <div className="ContentItemInterationsWrapper">
                                <div className="ContentItemInterationItem">
                                    <i className="fa fa-eye" />{' '}
                                    <span className="ContentItemMetricNumber">
                                    {getMetricStringValue(socialEngagements.views)}
                                </span>
                                </div>
                                <div className="ContentItemInterationItem">
                                    <i className="fa fa-heart-o" />{' '}
                                    <span className="ContentItemMetricNumber">
                                    {formatInsightNumber(socialEngagements.likes)}
                                </span>
                                </div>
                                <div className="ContentItemInterationItem">
                                    <i className="fa fa-comment" />{' '}
                                    <span className="ContentItemMetricNumber">
                                    {formatInsightNumber(socialEngagements.replies)}
                                </span>
                                </div>
                            </div>
                            {rating > 0 ? (
                                <div className="ContentItemInterationItem">
                                    <i className="fa fa-star" /> {rating}
                                </div>
                            ) : null}
                        </div>
                        <button
                            className="ContentItemViewButton"
                            onClick={() => {
                                setSelectedPost(content_metadata_id);
                            }}
                        >
                            View
                        </button>
                    </div>
                    {postedAt ? (
                        <div className="ContentItemPostDate">
                            <i className="fa fa-paper-plane" /> Posted on {formatDate(new Date(postedAt), {dateStyle: 'short'})}
                        </div>
                    ) : null}
                </div>
            );
        }

        return (
            <div
                className={postGridItemClasses}
                style={postStyle}
                onClick={() => {
                    setSelectedPost(content_metadata_id);
                }}
            >
                {playButtonOverlay}
                <div className="post-overlay">
                    {likesCommentsSection}
                    {thumbnailCenter}
                    {ratingSection}
                </div>
            </div>
        );
    }
}



// WEBPACK FOOTER //
// ./campaign_analytics/v2/campaign_analytics_page/posts/post_grid_item/index.js